/**
 * useIsTV
 * Returns true when the viewport is wide enough to be a TV or large display.
 * Detection is viewport-size only — no user agent sniffing.
 *
 * Threshold: ≥ 960px width.
 * Rationale: Silk Browser on Fire TV Stick can report CSS viewport widths in
 * the 960–1920px range depending on device pixel ratio and rendering mode.
 * 960px safely catches all Fire TV viewports while remaining above typical
 * tablet breakpoints (≤ 768px portrait, ≤ 1024px landscape).
 *
 * Used to:
 * - Apply TV safe-area padding (prevent overscan clipping)
 * - Lock the layout to desktop grid mode (prevent responsive collapse)
 * - Enable [data-tv-mode="true"] CSS density overrides
 */

import { useState, useEffect } from 'react';

const TV_VIEWPORT_MIN = 960;

export function useIsTV(): boolean {
  const [isTV, setIsTV] = useState<boolean>(
    () => typeof window !== 'undefined' && window.innerWidth >= TV_VIEWPORT_MIN
  );

  useEffect(() => {
    function handleResize() {
      setIsTV(window.innerWidth >= TV_VIEWPORT_MIN);
    }

    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isTV;
}
